import { useState, type FormEvent, type ReactNode } from 'react';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';

import { t } from '../../i18n';
import { toAuthFailure } from '../../lib/auth';
import { Alert, AlertDescription } from '../../ui/alert';
import { Button } from '../../ui/button';
import { Field, FieldLabel, FieldSeparator } from '../../ui/field';
import { Input } from '../../ui/input';
import { describeAuthFailure, useAuthSession } from '../auth/AuthProvider';
import { AuthFrame } from './AuthFrame';
import { GoogleButton } from './GoogleButton';
import { AUTH_ROUTE_PATHS, HOME_PATH } from './paths';

/**
 * Access is self-serve (platform design §17.2): this screen always offers a
 * path to `/sign-up`, never only to an operator who provisions accounts
 * by hand. Google is offered as a second method (architecture S1, §3)
 * alongside email/password, not instead of it.
 */
export function SignIn(): ReactNode {
  const { session } = useAuthSession();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { redirect?: unknown };
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // A signed-in caller is sent back to the page they were standing on when
  // their session dropped (`Forbidden.tsx`'s `SignInRequired`), or to the
  // application root when there is none -- never to any capability it would
  // take a role to reach: nothing client-side decides authorisation (Spec
  // §3), so this never routes by capability. `useMe`, inside the shell
  // mounted at HOME_PATH, is what resolves what a signed-in caller may do --
  // including recovering from a session Firebase had already restored.
  //
  // `redirect` is only ever trusted as an in-app path: it arrives on the URL,
  // so a value pointing off `/` is not honoured.
  const destination =
    typeof search.redirect === 'string' && search.redirect.startsWith('/')
      ? search.redirect
      : HOME_PATH;

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await session.signIn(email, password);
      await navigate({ to: destination });
    } catch (cause) {
      // Never a raw Firebase code, and a wrong password reads the same as an
      // unknown address -- see `toAuthFailure` in src/lib/auth.ts.
      setError(describeAuthFailure(toAuthFailure(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleGoogle(): Promise<void> {
    setError(null);
    setSubmitting(true);
    try {
      await session.signInWithGoogle();
      await navigate({ to: destination });
    } catch (cause) {
      setError(describeAuthFailure(toAuthFailure(cause)));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthFrame
      title={t('auth.signIn')}
      footer={{
        prompt: t('auth.signUp.prompt'),
        to: AUTH_ROUTE_PATHS.signUp,
        label: t('auth.signUp'),
      }}
    >
      <form onSubmit={(event) => void handleSubmit(event)} className="flex flex-col gap-4">
        <Field>
          <FieldLabel htmlFor="sign-in-email">{t('auth.email')}</FieldLabel>
          <Input
            id="sign-in-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </Field>

        <Field>
          <FieldLabel htmlFor="sign-in-password">{t('auth.password')}</FieldLabel>
          <Input
            id="sign-in-password"
            name="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          <div className="flex justify-end">
            <Button asChild variant="link" size="sm">
              <Link to={AUTH_ROUTE_PATHS.resetPassword}>{t('auth.forgotPassword')}</Link>
            </Button>
          </div>
        </Field>

        {error !== null && (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <Button type="submit" disabled={submitting}>
          {t('auth.submit')}
        </Button>
      </form>

      <FieldSeparator>{t('auth.or')}</FieldSeparator>

      <GoogleButton onClick={() => void handleGoogle()} disabled={submitting} />
    </AuthFrame>
  );
}
